import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { ApiError } from '../../api';
import { gamePromotionEndpoints, iconLabel } from '../../api-game-promotions';
import { Badge, Button } from '../../ui';
import { INPUT } from './shared';

interface Choice {
  game_promotion_id: string;
  name: string;
  icon: string;
  featured: boolean;
  sponsor_override: string;
  detail_override: string;
  source: 'RULE' | 'MANUAL' | 'NEW';
  rule_selects: boolean;
}

/**
 * A game's promotions (D-377), in place of D-368's three text fields: picked
 * from the Promotions library, one of them featured on the schedule row, each
 * with this game's own sponsor or detail when it differs.
 *
 * Taking off a promotion a rule gave the game excludes it rather than
 * unlinking it, so the rule cannot put it back — "no Dollar Monday on Labor
 * Day". Saved on its own button, because it is a list in its own table, not
 * a column of the event.
 */
export function PromotionsPicker({ eventId, editable, onError }: { eventId: string; editable: boolean; onError: (error: ApiError) => void }) {
  const queryClient = useQueryClient();
  const library = useQuery({ queryKey: ['game-promotions'], queryFn: gamePromotionEndpoints.list });
  const links = useQuery({ queryKey: ['event', eventId, 'promotions'], queryFn: () => gamePromotionEndpoints.eventLinks(eventId) });
  const [choices, setChoices] = useState<Choice[]>([]);
  const [excluded, setExcluded] = useState<string[]>([]);
  const [adding, setAdding] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!links.data) return;
    setChoices(
      links.data.links
        .filter((link) => !link.excluded)
        .map((link) => ({ game_promotion_id: link.game_promotion_id, name: link.name, icon: link.icon, featured: link.featured === 1, sponsor_override: link.sponsor_override ?? '', detail_override: link.detail_override ?? '', source: link.source, rule_selects: link.rule_selects })),
    );
    setExcluded(links.data.links.filter((link) => link.excluded).map((link) => link.name));
    setDirty(false);
  }, [links.data]);

  const save = useMutation({
    mutationFn: () =>
      gamePromotionEndpoints.setEventLinks(
        eventId,
        choices.map((choice) => ({
          game_promotion_id: choice.game_promotion_id,
          featured: choice.featured,
          sponsor_override: choice.sponsor_override.trim() ? choice.sponsor_override.trim() : null,
          detail_override: choice.detail_override.trim() ? choice.detail_override.trim() : null,
        })),
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['event', eventId, 'promotions'] }),
    onError,
  });

  const change = (next: Choice[]) => {
    setChoices(next);
    setDirty(true);
  };
  const available = (library.data?.promotions ?? []).filter((promotion) => !choices.some((choice) => choice.game_promotion_id === promotion.id));

  return (
    <div className="mt-4 space-y-3">
      <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Promotions</p>
      <p className="text-xs text-ink-500">
        From the <Link to="/promotions" className="text-brand hover:underline">Promotions library</Link>. The featured one is on the schedule row; the game page lists them all. Leave sponsor or detail empty to use the library’s.
      </p>
      {choices.length === 0 ? <p className="text-sm text-ink-500">No promotions on this game.</p> : null}
      <ul className="space-y-2">
        {choices.map((choice, index) => (
          <li key={choice.game_promotion_id} className="rounded-lg border border-line p-3">
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-medium">{choice.name}</span>
              <span className="text-xs text-ink-500">{iconLabel(choice.icon)}</span>
              {choice.source === 'RULE' ? <Badge tone="accent">By rule</Badge> : null}
              <label className="flex items-center gap-1 text-sm">
                <input type="radio" name={`featured-${eventId}`} checked={choice.featured} onChange={() => change(choices.map((entry, i) => ({ ...entry, featured: i === index })))} disabled={!editable} />
                Featured
              </label>
              {editable ? (
                <Button type="button" onClick={() => change(choices.filter((_, i) => i !== index))}>
                  {choice.rule_selects ? 'Exclude from this game' : 'Remove'}
                </Button>
              ) : null}
            </div>
            <div className="mt-2 flex flex-wrap gap-3">
              <input aria-label={`${choice.name}: this game's sponsor`} placeholder="This game's sponsor" className={`w-64 ${INPUT}`} value={choice.sponsor_override} maxLength={120} disabled={!editable} onChange={(e) => change(choices.map((entry, i) => (i === index ? { ...entry, sponsor_override: e.target.value } : entry)))} />
              <input aria-label={`${choice.name}: this game's detail`} placeholder="This game's detail" className={`min-w-72 flex-1 ${INPUT}`} value={choice.detail_override} maxLength={500} disabled={!editable} onChange={(e) => change(choices.map((entry, i) => (i === index ? { ...entry, detail_override: e.target.value } : entry)))} />
            </div>
          </li>
        ))}
      </ul>
      {excluded.length > 0 ? <p className="text-xs text-ink-500">Excluded here although a rule selects this game: {excluded.join(', ')}. Add it again to put it back.</p> : null}
      {editable ? (
        <div className="flex flex-wrap items-center gap-2">
          <select aria-label="Add a promotion" className={`w-64 ${INPUT}`} value={adding} onChange={(e) => setAdding(e.target.value)}>
            <option value="">Add a promotion…</option>
            {available.map((promotion) => (
              <option key={promotion.id} value={promotion.id}>
                {promotion.name}
              </option>
            ))}
          </select>
          <Button
            type="button"
            disabled={!adding}
            onClick={() => {
              const promotion = library.data?.promotions.find((entry) => entry.id === adding);
              if (!promotion) return;
              change([...choices, { game_promotion_id: promotion.id, name: promotion.name, icon: promotion.icon, featured: choices.length === 0, sponsor_override: '', detail_override: '', source: 'NEW', rule_selects: false }]);
              setAdding('');
            }}
          >
            Add
          </Button>
          <Button type="button" variant="primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save promotions'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
